import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, pointerWithin, rectIntersection, useDraggable, useDroppable, useSensor, useSensors, type CollisionDetection, type DragEndEvent } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangle, Archive, BookOpen, Check, ChevronDown, ChevronRight, CircleX, Database, ExternalLink, Eye, FileText, Folder, FolderTree, KeyRound, LayoutDashboard, LoaderCircle, RefreshCw, Search, Settings2, ShieldAlert, Trash2, X } from 'lucide-react';
import { displayCategory } from '../shared/domain';
import { STANDARD_CATEGORIES } from '../shared/schema';
import type { CatalogRecord, DirectoryNode, OperationResult, OverviewData, PendingRecord, RecordDetail, SnapshotBundle } from '../shared/types';
import { api, downloadSnapshot, type HealthState } from './lib/api';
import { formatSnapshotTimestamp, isSnapshotStale } from './lib/snapshot-freshness';
import { clearSnapshotCache } from './lib/snapshot-cache';

type Page = 'overview' | 'catalog' | 'pending' | 'operations';
type Filters = {
  q: string;
  project: string;
  type: string;
  status: string;
  category: string;
  projects: string;
  categories: string;
  missingProject: string;
  missingCategory: string;
  missingTitle: string;
  needsOrganization: string;
  nodeRecordIds: string[] | null;
};
const EMPTY_FILTERS: Filters = {
  q: '', project: '', type: '', status: '', category: '', projects: '', categories: '',
  missingProject: '', missingCategory: '', missingTitle: '', needsOrganization: '',
  nodeRecordIds: null,
};
const catalogCollisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
};

function cn(...values: Array<string | false | null | undefined>) { return values.filter(Boolean).join(' '); }
function formatDate(value: string | null) { return value ? new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value)) : '—'; }

const PAGE_SIZE_OPTIONS = [20, 40, 60] as const;

function Paginator({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: { page: number; totalPages: number; total: number; pageSize: number; onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void }) {
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    const window = new Set<number>([1, totalPages]);
    for (let near = Math.max(1, page - 1); near <= Math.min(totalPages, page + 1); near += 1) window.add(near);
    return [...window].sort((a, b) => a - b);
  }, [page, totalPages]);
  const start = totalPages === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return <nav className="paginator" aria-label="分页导航">
    <span className="paginator-range">{total === 0 ? '无结果' : `${start.toLocaleString('zh-CN')}-${end.toLocaleString('zh-CN')} / 共 ${total.toLocaleString('zh-CN')} 条`}</span>
    <div className="paginator-controls">
      <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)} aria-label="上一页"><ChevronRight size={14} style={{ transform: 'scaleX(-1)' }}/></button>
      <span className="page-indicator">第 {totalPages === 0 ? 0 : page} / {totalPages} 页</span>
      <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="下一页"><ChevronRight size={14}/></button>
      <span className="page-numbers">{pages.map((pageNumber, index) => <span key={pageNumber}>{index > 0 && pageNumber - pages[index - 1] > 1 && <span className="page-ellipsis">…</span>}<button type="button" className={cn('page-number', pageNumber === page && 'active')} onClick={() => onPageChange(pageNumber)} aria-label={`第 ${pageNumber} 页`}>{pageNumber}</button></span>)}</span>
      <select className="page-size-select" aria-label="每页显示条数" value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>{PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} 条/页</option>)}</select>
    </div>
  </nav>;
}

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  return <span className={cn('status-dot', ok ? 'ok' : 'bad')}><i />{label}</span>;
}

function StatePanel({ icon, title, detail, action }: { icon: ReactNode; title: string; detail: string; action?: ReactNode }) {
  return <div className="state-panel"><div className="state-icon">{icon}</div><h3>{title}</h3><p>{detail}</p>{action}</div>;
}

function Metric({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return <div className={cn('metric', danger && value > 0 && 'metric-danger')}><span>{label}</span><strong>{value.toLocaleString('zh-CN')}</strong></div>;
}

function Overview({ data, onNavigate }: { data: OverviewData; onNavigate: (filters: Partial<Filters>) => void }) {
  const totals = data.totals;
  return <div className="page-stack">
    {data.mappingErrors.length > 0 && <div className="alert danger"><ShieldAlert size={17} /><div><strong>字段映射异常</strong><span>{data.mappingErrors.join('；')}</span></div></div>}
    <section className="metric-strip" aria-label="库存统计">
      <Metric label="原文文档" value={totals.sources} />
      <Metric label="目录文档" value={totals.humanDocuments} />
      <Metric label="待确认" value={totals.pending} danger />
      <Metric label="车型数" value={totals.vehicleCount} />
      <Metric label="缺项目" value={totals.missingProject} danger />
      <Metric label="缺分类" value={totals.missingCategory} danger />
    </section>
    <section className="panel">
      <div className="section-heading"><div><span className="eyebrow">INVENTORY</span><h2>车型与项目</h2></div><span className="meta">点击行进入知识目录</span></div>
      <div className="table-wrap"><table><thead><tr><th>车型 / 项目</th><th>归组</th><th>原文</th><th>待确认</th><th>最近更新</th></tr></thead><tbody>
        {data.projects.map((row) => <tr key={row.project} onClick={() => onNavigate({ project: row.project })} tabIndex={0}>
          <td className="strong-cell">{row.project}</td><td><span className="tag neutral">{row.group}</span></td><td>{row.sourceCount}</td><td className={row.pendingCount ? 'danger-text' : ''}>{row.pendingCount}</td><td>{formatDate(row.updatedAt)}</td>
        </tr>)}
      </tbody></table></div>
    </section>
    <section className="panel matrix-panel">
      <div className="section-heading"><div><span className="eyebrow">COVERAGE</span><h2>车型 × 一级目录</h2></div><span className="meta">数字为原文文档数</span></div>
      <div className="matrix-scroll"><div className="matrix" style={{ gridTemplateColumns: `minmax(138px,1.4fr) repeat(${data.categories.length}, minmax(62px, .7fr))` }}>
        <div className="matrix-head sticky-x">车型</div>{data.categories.map((category) => <div className="matrix-head vertical" key={category} title={category}>{category.replace(/^分类-\d+_/, '')}</div>)}
        {Object.keys(data.matrix).sort().map((project) => <div className="matrix-row" key={project} style={{ display: 'contents' }}>
          <button className="matrix-project sticky-x" onClick={() => onNavigate({ project })}>{project}</button>
          {data.categories.map((category) => { const count = data.matrix[project]?.[category] ?? 0; return <button className={cn('matrix-cell', count > 0 && 'filled')} key={category} onClick={() => onNavigate({ project, category })} title={`${project} / ${category}: ${count}`}>{count || '·'}</button>; })}
        </div>)}
      </div></div>
    </section>
  </div>;
}

function DraggableRecord({ record, onOpen }: { record: CatalogRecord; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } = useDraggable({ id: record.recordId, data: { record } });
  return <article
    ref={setNodeRef}
    data-record-id={record.recordId}
    style={{ transform: CSS.Translate.toString(transform) }}
    className={cn('record-row', isDragging && 'dragging')}
    onClick={onOpen}
    onKeyDown={(event) => { if (event.target === event.currentTarget && event.key === 'Enter') onOpen(); }}
    tabIndex={0}
  >
    <button ref={setActivatorNodeRef} className="record-kind drag-handle" aria-label={`拖动 ${record.title}`} onClick={(event) => event.stopPropagation()} {...listeners} {...attributes}>{record.type === '知识点' ? <BookOpen size={16} /> : <FileText size={16} />}</button>
    <div className="record-main">
      {record.sourceUrl
        ? <a className="record-title-link" href={record.sourceUrl} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>{record.title}<ExternalLink size={13} aria-hidden="true" /></a>
        : <span className="record-title-text">{record.title}</span>}
      <span>{record.project ?? '未标项目'} · {record.category ? displayCategory(record.category) : '未分类'}{!record.sourceUrl && <em>原文链接缺失</em>}</span>
    </div>
    <span className={cn('tag', record.status === '需人工确认' ? 'warn' : 'neutral')}>{record.status ?? '状态缺失'}</span>
    <time>{formatDate(record.updatedAt)}</time>
    <button className="record-detail" aria-label={`查看 ${record.title} 详情`} title="查看详情" onClick={(event) => { event.stopPropagation(); onOpen(); }}><Eye size={16} /></button>
  </article>;
}

function TreeNode({ node, selected, onSelect, depth = 0 }: { node: DirectoryNode; selected: string; onSelect: (node: DirectoryNode) => void; depth?: number }) {
  const [open, setOpen] = useState(depth < 2);
  const { setNodeRef, isOver } = useDroppable({ id: node.path || 'display:root', data: { node }, disabled: !node.target });
  return <div className="tree-branch">
    <div ref={setNodeRef} data-drop-target={node.target ? 'true' : 'false'} className={cn('tree-node', selected === node.path && 'selected', isOver && 'drop-over')} style={{ paddingLeft: 8 + depth * 15 }}>
      <button className="tree-toggle" onClick={() => setOpen(!open)} aria-label={open ? '收起目录' : '展开目录'}>{node.children.length ? (open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>) : <span />}</button>
      <button className="tree-label" onClick={() => onSelect(node)}><Folder size={15}/><span>{node.name}</span>{typeof node.count === 'number' && <small>{node.count.toLocaleString('zh-CN')}</small>}</button>
    </div>
    {open && node.children.map((child) => <TreeNode key={child.path} node={child} selected={selected} onSelect={onSelect} depth={depth + 1}/>) }
  </div>;
}

function parseFilterList(value: string): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function Catalog({ tree, catalog, version, initialFilters, health, loggedIn, notify }: { tree: DirectoryNode; catalog: CatalogRecord[]; version: string; initialFilters: Filters; health: HealthState | null; loggedIn: boolean; notify: (message: string, tone?: 'ok'|'bad') => void }) {
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedPath, setSelectedPath] = useState('');
  const [detailRecord, setDetailRecord] = useState<CatalogRecord | null>(null);
  const [detail, setDetail] = useState<RecordDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 7 } }), useSensor(KeyboardSensor));
  const filtered = useMemo(() => {
    const q = filters.q.trim().toLocaleLowerCase('zh-CN');
    const projects = parseFilterList(filters.projects);
    const categories = parseFilterList(filters.categories);
    const nodeIdSet = filters.nodeRecordIds ? new Set(filters.nodeRecordIds) : null;
    return catalog.filter((record) => {
      if (!record.isDocument) return false;
      if (nodeIdSet && !nodeIdSet.has(record.recordId)) return false;
      if (q && !record.title.toLocaleLowerCase('zh-CN').includes(q)) return false;
      if (filters.project && record.project !== filters.project) return false;
      if (projects.length && (!record.project || !projects.includes(record.project))) return false;
      if (filters.type && record.type !== filters.type) return false;
      if (filters.status && record.status !== filters.status) return false;
      if (filters.category && record.category !== filters.category) return false;
      if (categories.length && (!record.category || !categories.includes(record.category))) return false;
      if (filters.missingProject && record.project !== null) return false;
      if (filters.missingCategory && record.category !== null) return false;
      if (filters.missingTitle && record.titleSource !== 'missing') return false;
      if (filters.needsOrganization && record.titleSource !== 'missing' && record.project !== null && record.category !== null) return false;
      return true;
    });
  }, [catalog, filters]);
  useEffect(() => { setPage(1); }, [filters]);

  const openDetail = async (record: CatalogRecord) => {
    setDetailRecord(record);
    setDetail(null);
    setDetailLoading(true);
    try {
      const loaded = await api.detail(version, record.recordId);
      setDetail(loaded);
    } catch (error) {
      notify(error instanceof Error ? error.message : '详情读取失败', 'bad');
    } finally {
      setDetailLoading(false);
    }
  };
  const selectTree = (node: DirectoryNode) => {
    setSelectedPath(node.path);
    // 使用 documentRecordIds 精确过滤
    if (node.documentRecordIds && node.documentRecordIds.length > 0) {
      setFilters((old) => ({
        ...old,
        nodeRecordIds: node.documentRecordIds ?? null,
        // 清除其他过滤条件，只用 nodeRecordIds
        project: '', category: '', projects: '', categories: '',
        missingProject: '', missingCategory: '', missingTitle: '', needsOrganization: '',
      }));
      return;
    }
    // fallback：节点没有 documentRecordIds 时使用 filters
    const nodeFilters = node.filters ?? {};
    const projects = nodeFilters.projects ?? [];
    const categories = nodeFilters.categories ?? [];
    setFilters((old) => ({
      ...old,
      nodeRecordIds: null,
      project: projects.length === 1 ? projects[0] : '',
      category: categories.length === 1 ? categories[0] : '',
      projects: projects.length > 1 ? JSON.stringify(projects) : '',
      categories: categories.length > 1 ? JSON.stringify(categories) : '',
      missingProject: nodeFilters.missingProject ? 'true' : '',
      missingCategory: nodeFilters.missingCategory ? 'true' : '',
      missingTitle: nodeFilters.missingTitle ? 'true' : '',
      needsOrganization: nodeFilters.needsOrganization ? 'true' : '',
    }));
  };
  const updateManualDirectoryFilter = (patch: Pick<Filters, 'project'> | Pick<Filters, 'category'>) => {
    setSelectedPath('');
    setFilters((old) => ({
      ...old,
      ...patch,
      nodeRecordIds: null,
      projects: '',
      categories: '',
      missingProject: '',
      missingCategory: '',
      missingTitle: '',
      needsOrganization: '',
    }));
  };
  const dragEnd = async (event: DragEndEvent) => {
    const record = event.active.data.current?.record as CatalogRecord | undefined;
    const node = event.over?.data.current?.node as DirectoryNode | undefined;
    if (!record || !node) return;
    if (!node.target) return notify('该展示分组聚合了多个原始目录，不能作为移动目标', 'bad');
    if (!loggedIn || !health?.write.enabled || !health.drive.connected) return notify(!loggedIn ? '请先登录管理员' : (health?.write.reason || health?.drive.reason || 'Drive 写操作不可用'), 'bad');
    try {
      const result = await api.transact(record.recordId, { action: '移动', expectedUpdatedAt: record.updatedAt, target: node.target });
      notify(result.ok ? `${result.message}；重建快照后展示将更新` : result.message, result.ok ? 'ok' : 'bad');
    } catch (error) { notify(error instanceof Error ? error.message : '移动失败', 'bad'); }
  };
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const items = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return <DndContext sensors={sensors} collisionDetection={catalogCollisionDetection} onDragEnd={(event) => void dragEnd(event)}><div className="catalog-layout">
    <aside className="tree-panel"><div className="tree-title"><FolderTree size={17}/><span>知识目录</span></div><TreeNode node={tree} selected={selectedPath} onSelect={selectTree}/></aside>
    <section className="catalog-main">
      <div className="filters">
        <label className="searchbox"><Search size={16}/><input aria-label="标题搜索" placeholder="搜索标题" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}/></label>
        <input aria-label="车型或项目" placeholder="车型 / 项目" value={filters.project} onChange={(e) => updateManualDirectoryFilter({ project: e.target.value })}/>
        <select aria-label="类型" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}><option value="">全部类型</option><option>原文资料</option></select>
        <select aria-label="状态" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">全部状态</option><option>已入库</option><option>已生成知识点</option><option>需人工确认</option><option>待整理</option></select>
        <select aria-label="一级目录" value={filters.category} onChange={(e) => updateManualDirectoryFilter({ category: e.target.value })}><option value="">全部一级目录</option>{STANDARD_CATEGORIES.map((item) => <option key={item} value={item}>{displayCategory(item)}</option>)}</select>
      </div>
      <div className="list-meta"><span>{filtered.length.toLocaleString('zh-CN')} 条结果</span><span>本地快照筛选 · 当前第 {currentPage} / {totalPages} 页</span></div>
      <div className="record-list-wrap">{items.map((record) => <DraggableRecord key={record.recordId} record={record} onOpen={() => void openDetail(record)}/>)}</div>
      {items.length === 0 && <StatePanel icon={<Archive/>} title="没有符合条件的记录" detail="这是真实空结果，不会自动套用默认类型或状态。"/>}
      <Paginator page={currentPage} totalPages={totalPages} total={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}/>
    </section>
  </div>{detailRecord && <DetailDrawer record={detailRecord} detail={detail} loading={detailLoading} onClose={() => setDetailRecord(null)}/>}</DndContext>;
}

function DetailDrawer({ record, detail, loading, onClose }: { record: CatalogRecord; detail: RecordDetail | null; loading: boolean; onClose: () => void }) {
  return <div className="drawer-backdrop" onMouseDown={onClose}><aside className="drawer" onMouseDown={(event) => event.stopPropagation()}>
    <div className="drawer-head"><span className="eyebrow">RECORD DETAIL</span><button onClick={onClose} aria-label="关闭"><X size={19}/></button></div>
    <h2>{record.title}</h2>
    {loading && !detail ? <div className="inline-loading"><LoaderCircle className="spin" size={18}/>按需读取详情…</div> : <dl>
      <dt>类型</dt><dd>{record.type ?? '未分类'}</dd>
      <dt>原文链接</dt><dd>{record.sourceUrl ? <a href={record.sourceUrl} target="_blank" rel="noopener noreferrer">打开原文 <ExternalLink size={14}/></a> : '原文链接缺失'}</dd>
      <dt>摘要 / 结论</dt><dd>{detail?.summary ?? record.summary ?? '暂无摘要'}</dd>
      <dt>录入批次</dt><dd>{detail?.batch ?? record.batch ?? '未填写'}</dd>
      <dt>系统更新时间</dt><dd>{record.updatedAt ? new Date(record.updatedAt).toLocaleString('zh-CN') : '未填写'}</dd>
    </dl>}
  </aside></div>;
}

function DecisionDialog({ records, action, onClose, onDone }: { records: PendingRecord[]; action: '纳入'|'不纳入'; onClose: () => void; onDone: (results: OperationResult[]) => void }) {
  const [target, setTarget] = useState<{ scope: string; project: string; category: string; subcategory: string; topic: string }>({ scope: 'F平台', project: '', category: STANDARD_CATEGORIES[0], subcategory: '', topic: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (action === '纳入' && (!target.project || !target.category)) return;
    setBusy(true);
    try {
      const operations = records.map((record) => ({ recordId: record.recordId, expectedUpdatedAt: record.updatedAt, action, ...(action === '纳入' ? { target } : {}) }));
      const result = await api.batchDecision(operations); onDone(result.results);
    } catch (error) { onDone(records.map((record) => ({ recordId: record.recordId, ok: false, phase: 'request', message: error instanceof Error ? error.message : '请求失败' }))); }
    finally { setBusy(false); }
  };
  return <div className="dialog-backdrop"><div className="dialog"><div className="dialog-head"><div><span className="eyebrow">BATCH ACTION</span><h2>{action} {records.length} 条记录</h2></div><button onClick={onClose}><X size={18}/></button></div>
    {action === '纳入' && <div className="form-grid"><label>所属目录<select value={target.scope} onChange={(e) => setTarget({ ...target, scope: e.target.value })}><option>F平台</option><option>G平台</option><option>D平台</option><option>平台化专项</option><option>流程规范</option><option>其他</option></select></label><label>车型 / 项目<input value={target.project} onChange={(e) => setTarget({ ...target, project: e.target.value })} placeholder="必填"/></label><label>一级目录<select value={target.category} onChange={(e) => setTarget({ ...target, category: e.target.value })}>{STANDARD_CATEGORIES.map((item) => <option key={item}>{item}</option>)}</select></label><label>二级目录<input value={target.subcategory} onChange={(e) => setTarget({ ...target, subcategory: e.target.value })}/></label><label className="span-2">三级目录 / 专题<input value={target.topic} onChange={(e) => setTarget({ ...target, topic: e.target.value })}/></label></div>}
    {action === '不纳入' && <div className="alert"><AlertTriangle size={18}/><span>只移除知识库内受管快捷方式并更新排除锚点，不删除原始文件。</span></div>}
    <div className="dialog-actions"><button className="secondary" onClick={onClose}>取消</button><button className="primary" disabled={busy || (action === '纳入' && !target.project)} onClick={() => void submit()}>{busy ? '处理中…' : '确认执行'}</button></div>
  </div></div>;
}

function Pending({ records, health, loggedIn, rebuilding, onBatchDone, notify }: { records: PendingRecord[]; health: HealthState | null; loggedIn: boolean; rebuilding: boolean; onBatchDone: (results: OperationResult[]) => void; notify: (message: string, tone?: 'ok'|'bad') => void }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [action, setAction] = useState<'纳入'|'不纳入'|null>(null);
  const [results, setResults] = useState<OperationResult[] | null>(null);
  const totalPages = Math.max(1, Math.ceil(records.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const items = records.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const selectedRecords = items.filter((item) => selected.has(item.recordId));
  const canWrite = loggedIn && Boolean(health?.write.enabled && health.drive.connected);
  return <div className="page-stack"><div className="section-heading"><div><span className="eyebrow">REVIEW QUEUE</span><h2>待确认</h2></div><span className="meta">严格口径：整理状态=需人工确认 且 人工确认为空 · 共 {records.length} 条</span></div>
    {!loggedIn && <div className="alert"><KeyRound size={18}/><span>未登录管理员，写操作不可用。请点击右上角设置图标登录。</span></div>}
    {loggedIn && !canWrite && <div className="alert danger"><ShieldAlert size={18}/><span>{health?.write.enabled === false ? '写入未开启（WRITE_ENABLED=false）' : (health?.drive.reason || '写入能力未连接')}</span></div>}
    {rebuilding && <div className="alert"><LoaderCircle className="spin" size={18}/><span>正在重建数据快照…</span></div>}
    <div className="batchbar"><label><input type="checkbox" checked={items.length > 0 && items.every((item) => selected.has(item.recordId))} onChange={(e) => setSelected(e.target.checked ? new Set([...selected, ...items.map((item) => item.recordId)]) : new Set([...selected].filter((id) => !items.some((item) => item.recordId === id))))}/>选择当前页</label><span>已选 {selected.size} · 当前页 {items.length} · 共 {records.length}</span><button disabled={!canWrite || selected.size === 0 || rebuilding} onClick={() => setAction('纳入')}><Check size={15}/>纳入</button><button disabled={!canWrite || selected.size === 0 || rebuilding} onClick={() => setAction('不纳入')} className="danger-button"><CircleX size={15}/>不纳入</button></div>
    <div className="pending-list">{items.map((record) => <label className="pending-row" data-record-id={record.recordId} key={record.recordId}><input type="checkbox" checked={selected.has(record.recordId)} onChange={(e) => { const next = new Set(selected); if (e.target.checked) next.add(record.recordId); else next.delete(record.recordId); setSelected(next); }}/><div><strong>{record.title}</strong><span>{record.status ?? '需人工确认'}</span></div><small>{record.project ?? '未标项目'}</small><time>{formatDate(record.updatedAt)}</time></label>)}</div>
    {items.length === 0 && <StatePanel icon={<Check/>} title="当前没有待确认记录" detail="这里仅展示真实待确认且人工确认为空的记录。"/>}
    <Paginator page={currentPage} totalPages={totalPages} total={records.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}/>
    {action && <DecisionDialog records={selectedRecords} action={action} onClose={() => setAction(null)} onDone={(nextResults) => { setAction(null); setResults(nextResults); setSelected(new Set()); const successCount = nextResults.filter((r) => r.ok).length; if (successCount > 0) notify(`${action}完成：成功 ${successCount} 条${nextResults.length - successCount > 0 ? `，失败 ${nextResults.length - successCount} 条` : ''}`); void onBatchDone(nextResults); }}/>}
    {results && <ResultPanel results={results} onClose={() => setResults(null)}/>}
  </div>;
}

function ResultPanel({ results, onClose }: { results: OperationResult[]; onClose: () => void }) {
  const success = results.filter((item) => item.ok).length;
  return <div className="result-panel"><div><strong>{success === results.length ? '操作完成' : success ? '部分成功' : '操作失败'}</strong><span>成功 {success} · 失败 {results.length - success}</span></div><button onClick={onClose}><X size={16}/></button><ul>{results.filter((item) => !item.ok).map((item) => <li key={item.recordId}><code>{item.recordId}</code> {item.phase}：{item.message}{item.rollback && `；回滚${item.rollback.ok ? '成功' : '失败'}`}</li>)}</ul></div>;
}

function Operations({ items }: { items: SnapshotBundle['operations'] }) {
  return <div className="page-stack"><div className="section-heading"><div><span className="eyebrow">WEEKLY RUNS</span><h2>运营状态</h2></div><span className="meta">前端刷新 ≠ Qoder 周度轮询</span></div><div className="alert"><Database size={17}/><span>周度验收表只保存轮询运行统计，不作为库存统计来源。</span></div>
    <div className="panel table-wrap"><table><thead><tr><th>周期</th><th>录入范围</th><th>新增原文</th><th>新增知识点</th><th>待确认</th><th>待开权</th><th>状态</th><th>结论 / 动作</th><th>生成时间</th></tr></thead><tbody>{items.map((row) => <tr key={row.recordId}><td>{row.cycle ?? '—'}</td><td>{row.scope.join('、') || '—'}</td><td>{row.newSources ?? '—'}</td><td>{row.newKnowledge ?? '—'}</td><td>{row.pending ?? '—'}</td><td>{row.waitingAccess ?? '—'}</td><td><span className={cn('tag', row.status === '阻塞' ? 'bad' : row.status === '关注' ? 'warn' : 'ok')}>{row.status ?? '未知'}</span></td><td className="conclusion">{row.conclusion ?? '—'}</td><td>{formatDate(row.generatedAt)}</td></tr>)}</tbody></table></div>
  </div>;
}

export default function App() {
  const [page, setPage] = useState<Page>('overview');
  const [bundle, setBundle] = useState<SnapshotBundle | null>(null);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [tree, setTree] = useState<DirectoryNode | null>(null);
  const [health, setHealth] = useState<HealthState | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loggedIn, setLoggedIn] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [clock, setClock] = useState(() => new Date());
  const [toast, setToast] = useState<{ message: string; tone: 'ok'|'bad' } | null>(null);
  const notify = useCallback((message: string, tone: 'ok'|'bad' = 'ok') => { setToast({ message, tone }); window.setTimeout(() => setToast(null), 4200); }, []);
  const snapshotStale = bundle ? isSnapshotStale(bundle.meta.generatedAt, clock) : false;

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    void api.session().then((result) => setLoggedIn(result.loggedIn)).catch(() => undefined);
  }, []);

  const activateBundle = useCallback((next: SnapshotBundle) => {
    setBundle(next);
    setOverview(next.summary);
    setTree(next.directory);
  }, []);

  const installSnapshot = useCallback(async (meta: SnapshotBundle['meta'], signal?: AbortSignal) => {
    const next = await downloadSnapshot(meta, signal, (summary, directory) => {
      setOverview((current) => current ?? summary);
      setTree((current) => current ?? directory);
    });
    activateBundle(next);
    return next;
  }, [activateBundle]);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        void api.health(controller.signal).then(setHealth).catch(() => undefined);
        const result = await api.snapshotMeta(undefined, controller.signal);
        if (result.meta) {
          await installSnapshot(result.meta, controller.signal);
        }
      } catch (nextError) {
        setOverview(null);
        setTree(null);
        setError(nextError instanceof Error ? nextError.message : '快照加载失败');
      } finally {
        setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [installSnapshot]);

  const refresh = useCallback(async () => {
    setChecking(true);
    setError('');
    try {
      const result = await api.snapshotMeta();
      if (result.notModified || result.meta?.version === bundle?.version) notify('当前已是最新快照');
      else if (result.meta) {
        await installSnapshot(result.meta);
        notify('只读数据快照已更新');
      }
    } catch (nextError) {
      if (!bundle) setError(nextError instanceof Error ? nextError.message : '快照检查失败');
      else notify('版本检查失败，继续使用当前快照', 'bad');
    } finally {
      setChecking(false);
    }
  }, [bundle, installSnapshot, notify]);

  const clearAndReload = async () => {
    setLoading(true);
    setError('');
    try {
      setBundle(null);
      setOverview(null);
      setTree(null);
      const result = await api.snapshotMeta();
      if (!result.meta) throw new Error('服务端未返回可用快照');
      await installSnapshot(result.meta);
      notify('已重新下载最新快照');
    } catch (nextError) {
      setBundle(null);
      setOverview(null);
      setTree(null);
      setError(nextError instanceof Error ? nextError.message : '重新下载失败');
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    if (!loginPassword) return notify('请输入管理员口令', 'bad');
    setLoginBusy(true);
    try {
      await api.login(loginPassword);
      setLoggedIn(true);
      setLoginPassword('');
      void api.health().then(setHealth).catch(() => undefined);
      notify('管理员登录成功');
    } catch (nextError) {
      notify(nextError instanceof Error ? nextError.message : '登录失败', 'bad');
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
      setLoggedIn(false);
      void api.health().then(setHealth).catch(() => undefined);
      notify('已登出');
    } catch (nextError) {
      notify(nextError instanceof Error ? nextError.message : '登出失败', 'bad');
    }
  };

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const result = await api.rebuildSnapshot();
      await installSnapshot(result.meta);
      notify(`快照 ${result.meta.version} 已生成并启用`);
    } catch (nextError) {
      notify(nextError instanceof Error ? nextError.message : '快照重建失败', 'bad');
    } finally {
      setRebuilding(false);
    }
  };

  const onBatchDone = useCallback(async (results: OperationResult[]) => {
    const successIds = new Set(results.filter((r) => r.ok).map((r) => r.recordId));
    if (successIds.size > 0 && bundle) {
      // Immediately remove successful items from local state for instant feedback.
      const nextBundle = {
        ...bundle,
        pending: bundle.pending.filter((p) => !successIds.has(p.recordId)),
        catalog: bundle.catalog.map((c) => successIds.has(c.recordId) ? { ...c, status: '已入库' } : c),
      };
      activateBundle(nextBundle);
    }
    if (successIds.size > 0) {
      // Rebuild snapshot to reflect changes.
      try {
        setRebuilding(true);
        const result = await api.rebuildSnapshot();
        await installSnapshot(result.meta);
        notify(`操作完成；快照 ${result.meta.version} 已更新`);
      } catch (nextError) {
        notify(nextError instanceof Error ? nextError.message : '操作完成但快照重建失败', 'bad');
      } finally {
        setRebuilding(false);
      }
    }
  }, [bundle, activateBundle, installSnapshot, notify]);

  const navigateCatalog = (next: Partial<Filters>) => { setFilters({ ...EMPTY_FILTERS, ...next }); setPage('catalog'); };
  const nav = useMemo(() => [
    { id: 'overview' as const, label: '车型总览', icon: <LayoutDashboard size={17}/> },
    { id: 'catalog' as const, label: '知识目录', icon: <FolderTree size={17}/> },
    { id: 'pending' as const, label: '待确认', icon: <AlertTriangle size={17}/>, badge: overview?.totals.pending },
    { id: 'operations' as const, label: '运营状态', icon: <Database size={17}/> },
  ], [overview?.totals.pending]);

  return <div className="app-shell">
    <aside className="sidebar"><div className="brand"><div className="brand-mark">海</div><div><strong>海外知识库</strong><span>Knowledge Operations</span></div></div><nav>{nav.map((item) => <button key={item.id} className={page === item.id ? 'active' : ''} onClick={() => setPage(item.id)}>{item.icon}<span>{item.label}</span>{item.badge ? <b>{item.badge}</b> : null}</button>)}</nav><div className="sidebar-foot"><StatusDot ok={Boolean(bundle)} label="KV 快照"/></div></aside>
    <main><header className="topbar"><div><span className="eyebrow">OVERSEAS KNOWLEDGE SYSTEM</span><h1>{nav.find((item) => item.id === page)?.label}</h1></div><div className="top-actions"><div className="snapshot-status" aria-label="数据快照状态"><span>数据版本：<code>{bundle?.meta.version ?? '未加载'}</code></span><span>更新时间：{bundle ? formatSnapshotTimestamp(bundle.meta.generatedAt) : '未加载'}</span>{snapshotStale && <span className="snapshot-stale"><AlertTriangle size={12}/>数据可能已过期</span>}</div><button className="icon-button" onClick={() => setShowSettings(true)} aria-label="管理员设置"><Settings2 size={17}/></button><button className="refresh-button" onClick={() => void refresh()} disabled={checking || loading}><RefreshCw className={checking ? 'spin' : ''} size={16}/>检查更新</button></div></header>
      <div className="content">{loading && !overview ? <StatePanel icon={<LoaderCircle className="spin"/>} title="正在下载只读快照" detail="优先加载总览与目录；普通访问不会读取 Base。"/> : error && !overview ? <StatePanel icon={<ShieldAlert/>} title="快照不可用" detail={error} action={<button className="primary" onClick={() => void refresh()}>重试</button>}/> : overview ? <>{loading && !bundle && <div className="alert"><LoaderCircle className="spin" size={17}/><span>总览与目录已就绪，正在校验并缓存完整快照…</span></div>}{page === 'overview' && <Overview data={overview} onNavigate={navigateCatalog}/>} {page === 'catalog' && bundle && tree && <Catalog tree={tree} catalog={bundle.catalog} version={bundle.version} initialFilters={filters} health={health} loggedIn={loggedIn} notify={notify}/>} {page === 'pending' && bundle && <Pending records={bundle.pending} health={health} loggedIn={loggedIn} rebuilding={rebuilding} onBatchDone={onBatchDone} notify={notify}/>} {page === 'operations' && bundle && <Operations items={bundle.operations}/>}</> : null}</div>
    </main>
    {showSettings && <div className="dialog-backdrop"><div className="dialog small" role="dialog" aria-modal="true" aria-labelledby="admin-settings-title"><div className="dialog-head"><div><span className="eyebrow">ADMIN</span><h2 id="admin-settings-title">管理员</h2></div><button onClick={() => setShowSettings(false)} aria-label="关闭管理员设置"><X size={18}/></button></div>
      {loggedIn ? <><div className="alert"><KeyRound size={17}/><span>管理员已登录{health?.write.enabled ? '；写入已开启' : '；写入未开启（WRITE_ENABLED=false）'}</span></div><button className="secondary settings-action" disabled={rebuilding} onClick={() => void rebuild()}><RefreshCw className={rebuilding ? 'spin' : ''} size={16}/>{rebuilding ? '正在重建…' : '重建数据快照'}</button><button className="secondary settings-action danger-button" onClick={() => void logout()}>登出</button></> : <><label className="key-input">管理员口令<input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="输入口令后回车" onKeyDown={(e) => { if (e.key === 'Enter') void login(); }}/></label><button className="primary settings-action" disabled={loginBusy} onClick={() => void login()}>{loginBusy ? '验证中…' : '登录'}</button></>}
      <button className="secondary settings-action" onClick={() => void clearAndReload()}><Trash2 size={16}/>清除缓存并重新下载</button>
      <div className="dialog-actions"><button className="primary" onClick={() => setShowSettings(false)}>完成</button></div>
    </div></div>}
    {toast && <div className={cn('toast', toast.tone)}>{toast.tone === 'ok' ? <Check size={16}/> : <AlertTriangle size={16}/>} {toast.message}</div>}
  </div>;
}
